"use client";

import * as React from "react";
import { Button } from "./button";
import { LiquidPill } from "./liquid-pill";
import { ArrowLeft } from "lucide-react";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";

export interface PageHeaderAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "outline" | "destructive";
}

export interface PageHeaderProps {
  title: string;
  description?: string | React.ReactNode;
  enableBack?: boolean;
  actions?: PageHeaderAction[];
  tags?: string[];
  className?: string;
}

export function PageHeader({
  title,
  description,
  enableBack = false,
  actions = [],
  tags = [],
  className = "",
}: PageHeaderProps) {
  const router = useRouter();
  const { t } = useTranslation("common");

  return (
    <div className={`mb-4 space-y-2 ${className}`}>
      {enableBack && (
        <button
          onClick={() => router.back()}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("Volver")}
        </button>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
            {title}
          </h1>
          {description && (
            <p className="text-sm text-slate-600 dark:text-zinc-400">
              {description}
            </p>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {tags.map((tag) => (
                <LiquidPill key={tag} text={tag} variant="slate" size="xs" />
              ))}
            </div>
          )}
        </div>

        {actions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {actions.map((action, i) => (
              <Button
                key={i}
                variant={action.variant || (i === 0 ? "primary" : "secondary")}
                size="sm"
                onClick={action.onClick}
                loading={action.loading}
                disabled={action.disabled}
                leftIcon={action.icon}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
